import {paginatedApiRequest} from './api';
import {adminActivitiesEndpoint} from '../constants/endpoints';

export type ActivityType = 'place' | 'event';

export type ActivitySortBy = 'name' | 'going' | 'lastUpdated' | 'date';

export type SortDirection = 'asc' | 'desc';

export interface AdminActivity {
  id: string;
  type: ActivityType;
  name: string;
  photoUri?: string | null;
  city?: string | null;
  street?: string | null;
  organizerId?: string | null;
  organizerName?: string | null;
  going: number;
  rating?: number | null;
  lastUpdated: string;
  // Events only; null for places and for recurring events without a fixed date.
  dateTimeStart?: string | null;
  dateTimeStop?: string | null;
}

export interface AdminActivitiesFilters {
  keyword: string;
  // Undefined returns both places and events.
  type?: ActivityType;
  sortBy: ActivitySortBy;
  sortDirection: SortDirection;
  pageNumber: number;
  pageSize: number;
}

export interface AdminActivitiesPage {
  items: AdminActivity[];
  totalItems: number;
  maxPage: number;
}

const emptyPage: AdminActivitiesPage = {items: [], totalItems: 0, maxPage: 0};

export const fetchAdminActivities = async (
  filters: AdminActivitiesFilters,
): Promise<AdminActivitiesPage> => {
  let failed = false;

  const response = await paginatedApiRequest<AdminActivity[]>(
    {
      endpoint: adminActivitiesEndpoint,
      method: 'GET',
      queryParams: {
        keyword: filters.keyword || undefined,
        type: filters.type,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
      },
    },
    {
      errorMessage: 'Failed to fetch activities',
      onError: () => {
        failed = true;
      },
    },
  );

  if (failed) {
    throw new Error('Failed to fetch activities');
  }

  // An empty result does not come back as a paginated payload.
  if (!response) {
    return emptyPage;
  }

  return {
    // The API names the type "Place" / "Event"; the screens compare lowercase.
    items: (response.data ?? []).map(item => ({
      ...item,
      type: String(item.type).toLowerCase() as ActivityType,
    })),
    totalItems: response.totalItems,
    maxPage: response.maxPage,
  };
};
